// src/components/production/PrazoDetalhesModal.js
import React, { useState, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  X,
  Clock,
  Calendar,
  AlertTriangle,
  CheckCircle2,
  TrendingDown,
  Layers,
  ShieldCheck,
  User,
  ArrowRight,
  History,
  Activity,
  RotateCcw,
  Sparkles,
} from "lucide-react";
import { useProductionContext } from "../../contexts/ProductionContext";
import { getModelFromSku } from "../../utils/skuUtils";
import {
  analisarPrazo,
  formatDataComDiaSemana,
  formatDiasUteis,
  ETAPAS_CANONICAS,
  STATUS_PRIORITY,
} from "../../utils/prazoEngine";
import { PRODUCTION_STATUS_CONFIG } from "../../config/productionStatuses";

export default function PrazoDetalhesModal({ order, isOpen, onClose }) {
  const { prazosMap = {}, temposEtapaMap = {} } = useProductionContext() || {};

  // Extrai lista de itens do pedido (se for pedido com múltiplos itens ou único)
  const rawItems = useMemo(() => {
    if (!order) return [];
    if (Array.isArray(order.items) && order.items.length > 0) {
      return order.items;
    }
    return [order];
  }, [order]);

  // Executa análise temporal individual para cada item do pedido
  const itemsAnalises = useMemo(() => {
    return rawItems.map((item, idx) => {
      const itemSku =
        item.sku ||
        item.order?.sku ||
        (idx === 0 ? order.sku || order.order?.sku : "") ||
        "";
      const itemModelo = getModelFromSku(itemSku);
      const prazoRecord = itemModelo ? prazosMap[itemModelo] : null;
      const prazoPrometido = prazoRecord?.prazo_prometido;
      const fatorRisco = prazoRecord?.fator_risco || 0;

      const dataCompra =
        item.customCreatedAt ||
        item.createdAt ||
        item.created_at ||
        item.date ||
        item.created ||
        order.customCreatedAt ||
        order.createdAt ||
        order.order?.created_at ||
        order.date ||
        order.created;

      const isPrazoTemporalStatus = (s) =>
        ["ATRASADO", "VAI_ATRASAR", "APERTADO", "NO_PRAZO", "EM_REVISAO", "SEM_PRAZO", "CANCELADO"].includes(
          String(s || "").trim().toUpperCase()
        );

      const statusAtual =
        (!isPrazoTemporalStatus(item.etapaProducao) && item.etapaProducao) ||
        (!isPrazoTemporalStatus(item.statusAtual) && item.statusAtual) ||
        (!isPrazoTemporalStatus(item.production_step) && item.production_step) ||
        (!isPrazoTemporalStatus(item.status) && item.status) ||
        (!isPrazoTemporalStatus(item.step) && item.step) ||
        item.logisticsStatus ||
        (!isPrazoTemporalStatus(order.etapaProducao) && order.etapaProducao) ||
        (!isPrazoTemporalStatus(order.production_step) && order.production_step) ||
        (!isPrazoTemporalStatus(order.status) && order.status) ||
        "SOLICITACAO";

      const isIntercepted = Boolean(
        item.isInterceptedPE ||
        item.isIntercepted ||
        order.isInterceptedPE ||
        order.isIntercepted
      );

      const analise = analisarPrazo({
        dataCompra,
        statusAtual,
        prazoPrometido,
        temposEtapa: temposEtapaMap,
        modelo: itemModelo,
        isIntercepted,
        fatorRisco,
      });

      // Extrai histórico de transições de etapas para este item
      const messages = Array.isArray(item.full_data?.messages)
        ? item.full_data.messages
        : Array.isArray(item.messages)
        ? item.messages
        : Array.isArray(order.full_data?.messages)
        ? order.full_data.messages
        : Array.isArray(order.messages)
        ? order.messages
        : [];

      const transitions = messages
        .filter(
          (m) =>
            m &&
            m.type === "system" &&
            m.text &&
            m.text.includes("Etapa alterada")
        )
        .map((m) => {
          const match = m.text.match(/de\s+"([^"]+)"\s+para\s+"([^"]+)"/i);
          let dateFormatted = "-";
          if (m.timestamp) {
            try {
              const d = new Date(m.timestamp);
              dateFormatted = d.toLocaleString("pt-BR", {
                day: "2-digit",
                month: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
              });
            } catch {}
          }
          return {
            from: match ? match[1] : null,
            to: match ? match[2] : null,
            rawText: m.text,
            author: m.author || "Sistema",
            dateFormatted,
          };
        });

      const itemName =
        item.product_name ||
        item.name ||
        item.item?.name ||
        (itemSku ? `Peça (${itemSku})` : `Item #${idx + 1}`);

      return {
        item,
        index: idx,
        sku: itemSku,
        modelo: itemModelo,
        name: itemName,
        dataCompra,
        statusAtual,
        isIntercepted,
        prazoRecord,
        fatorRisco,
        analise,
        transitions,
      };
    });
  }, [rawItems, order, prazosMap, temposEtapaMap]);

  // Calcula imediatamente o índice do item de maior criticidade
  const mostCriticalIdx = useMemo(() => {
    if (!itemsAnalises || itemsAnalises.length <= 1) return 0;
    let bestIdx = 0;
    let bestPriority = 99;
    itemsAnalises.forEach((it, idx) => {
      const priority = STATUS_PRIORITY[it.analise.status] || 99;
      if (priority < bestPriority) {
        bestPriority = priority;
        bestIdx = idx;
      }
    });
    return bestIdx;
  }, [itemsAnalises]);

  // Estado da seleção manual pelo usuário (null indica que deve usar o item mais crítico por padrão)
  const [userSelectedIndex, setUserSelectedIndex] = useState(null);

  // Ao abrir o modal ou trocar de pedido, reseta a seleção manual para sempre focar no mais crítico
  useEffect(() => {
    setUserSelectedIndex(null);
  }, [isOpen, order]);

  if (!isOpen || !order) return null;

  const activeIndex =
    userSelectedIndex !== null &&
    userSelectedIndex >= 0 &&
    userSelectedIndex < itemsAnalises.length
      ? userSelectedIndex
      : mostCriticalIdx;

  const currentItemData = itemsAnalises[activeIndex] || itemsAnalises[0];
  if (!currentItemData) return null;

  const {
    sku,
    modelo,
    name: itemName,
    dataCompra,
    statusAtual,
    fatorRisco,
    analise,
    transitions,
  } = currentItemData;

  // Identifica onde a etapa atual do item selecionado se situa na esteira canônica
  const etapaAtual = analise.etapaMapeada || statusAtual;
  const currentIndex = ETAPAS_CANONICAS.indexOf(etapaAtual);
  const isProntoOuEnviado =
    analise.status === "PRONTO" ||
    analise.status === "ENVIADO" ||
    analise.isFinalizado;

  // Configuração visual por Status Geral
  const getStatusBadge = (st) => {
    switch (st) {
      case "PRONTO":
        return {
          label: "PRONTO",
          bg: "bg-emerald-100 text-emerald-800 border-emerald-300",
        };
      case "ENVIADO":
        return {
          label: "ENVIADO",
          bg: "bg-blue-100 text-blue-800 border-blue-300",
        };
      case "EM_REVISAO":
        return {
          label: "REVISÃO",
          bg: "bg-amber-100 text-amber-900 border-amber-300",
        };
      case "ATRASADO":
        return {
          label: "ATRASADO",
          bg: "bg-red-100 text-red-800 border-red-300",
        };
      case "VAI_ATRASAR":
        return {
          label: "VAI ATRASAR",
          bg: "bg-orange-100 text-orange-800 border-orange-300",
        };
      case "APERTADO":
        return {
          label: "APERTADO",
          bg: "bg-amber-100 text-amber-800 border-amber-300",
        };
      case "NO_PRAZO":
        return {
          label: "NO PRAZO",
          bg: "bg-emerald-100 text-emerald-800 border-emerald-300",
        };
      default:
        return {
          label: "SEM PRAZO",
          bg: "bg-slate-100 text-slate-700 border-slate-300",
        };
    }
  };

  // Cores e rótulos do Nível de Risco
  const RISCO_CONFIG = {
    CONCLUIDO: {
      label: "PRODUÇÃO CONCLUÍDA",
      bg: "bg-emerald-600 text-white",
      border: "border-emerald-300",
      cardBg: "bg-emerald-50/80",
    },
    CRITICO: {
      label: "RISCO CRÍTICO",
      bg: "bg-red-500 text-white",
      border: "border-red-300",
      cardBg: "bg-red-50/70",
    },
    MUITO_ALTO: {
      label: "RISCO MUITO ALTO",
      bg: "bg-orange-500 text-white",
      border: "border-orange-300",
      cardBg: "bg-orange-50/70",
    },
    ALTO: {
      label: "RISCO ALTO",
      bg: "bg-amber-500 text-white",
      border: "border-amber-300",
      cardBg: "bg-amber-50/70",
    },
    MODERADO: {
      label: "RISCO MODERADO",
      bg: "bg-yellow-500 text-slate-900 font-bold",
      border: "border-yellow-300",
      cardBg: "bg-yellow-50/70",
    },
    MEDIO: {
      label: "RISCO MÉDIO",
      bg: "bg-blue-500 text-white",
      border: "border-blue-300",
      cardBg: "bg-blue-50/70",
    },
    BAIXO: {
      label: "BAIXO RISCO",
      bg: "bg-emerald-600 text-white",
      border: "border-emerald-300",
      cardBg: "bg-emerald-50/70",
    },
    REVISAO: {
      label: "EM REVISÃO",
      bg: "bg-amber-600 text-white",
      border: "border-amber-300",
      cardBg: "bg-amber-50/80",
    },
    DESCONHECIDO: {
      label: "SEM PRAZO",
      bg: "bg-slate-400 text-white",
      border: "border-slate-300",
      cardBg: "bg-slate-50",
    },
  };

  const riscoAtual =
    RISCO_CONFIG[analise.nivelRisco] || RISCO_CONFIG.DESCONHECIDO;

  const orderNumDisplay =
    order.orderNumber ||
    order.order?.number ||
    order.order_number ||
    order.id ||
    order.items?.[0]?.order?.number ||
    "-";

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[10005] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER DO MODAL */}
        <div className="bg-slate-900 text-white p-5 flex items-start justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Clock className="text-blue-400" size={20} />
              <h2 className="text-base md:text-lg font-black tracking-tight">
                Análise Temporal de Prazo da Oficina
              </h2>
            </div>
            <div className="text-xs text-slate-400 flex flex-wrap items-center gap-2">
              <span>Pedido #{orderNumDisplay}</span>
              {itemsAnalises.length > 1 && (
                <>
                  <span>•</span>
                  <span className="bg-indigo-500/20 text-indigo-300 px-2 py-0.5 rounded text-[10px] font-extrabold flex items-center gap-1">
                    <Layers size={11} /> {itemsAnalises.length} Itens no Pedido
                  </span>
                </>
              )}
              <span>•</span>
              <span className="font-mono text-blue-300 font-bold">
                {sku || "Sem SKU"}
              </span>
              {modelo && (
                <>
                  <span>•</span>
                  <span className="bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded text-[10px] font-bold">
                    {modelo}
                  </span>
                </>
              )}
            </div>
          </div>

          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
          >
            <X size={20} />
          </button>
        </div>

        {/* SELETOR DE ITENS (ABAS) - EXIBIDO QUANDO O PEDIDO CONTÉM MAIS DE UM ITEM */}
        {itemsAnalises.length > 1 && (
          <div className="bg-slate-800 border-b border-slate-700/80 px-4 py-2.5 flex items-center gap-2 overflow-x-auto custom-scrollbar">
            <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider shrink-0 mr-1 flex items-center gap-1">
              <Layers size={12} className="text-blue-400" />
              Itens ({itemsAnalises.length}):
            </span>
            {itemsAnalises.map((it, idx) => {
              const isSelected = idx === activeIndex;
              const badge = getStatusBadge(it.analise.status);
              return (
                <button
                  key={idx}
                  onClick={() => setUserSelectedIndex(idx)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs transition-all shrink-0 cursor-pointer border ${
                    isSelected
                      ? "bg-blue-600 border-blue-400 text-white font-black shadow-md scale-102"
                      : "bg-slate-700/70 hover:bg-slate-700 border-slate-600 text-slate-300 hover:text-white"
                  }`}
                >
                  <span className="font-mono font-bold">
                    {it.sku || `Item #${idx + 1}`}
                  </span>
                  {it.modelo && (
                    <span
                      className={`text-[10px] px-1.5 py-0.2 rounded font-bold ${
                        isSelected
                          ? "bg-blue-700 text-blue-100"
                          : "bg-slate-600 text-slate-300"
                      }`}
                    >
                      {it.modelo}
                    </span>
                  )}
                  <span
                    className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded border ${badge.bg}`}
                  >
                    {badge.label}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* CORPO COM SCROLL */}
        <div className="p-6 overflow-y-auto space-y-6 custom-scrollbar text-xs">
          {/* VISÃO COMPARATIVA DE TODOS OS ITENS (GRID RÁPIDO) */}
          {itemsAnalises.length > 1 && (
            <div className="bg-gradient-to-r from-slate-50 to-blue-50/40 border border-slate-200 rounded-xl p-3.5 space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="text-xs font-black text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                  <Layers size={14} className="text-blue-600" />
                  Comparativo de Itens do Pedido
                </span>
                <span className="text-[10px] text-slate-500 font-medium">
                  Clique em um item abaixo para alternar a análise detalhada
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {itemsAnalises.map((it, idx) => {
                  const isSelected = idx === activeIndex;
                  const badge = getStatusBadge(it.analise.status);
                  return (
                    <div
                      key={idx}
                      onClick={() => setUserSelectedIndex(idx)}
                      className={`p-2.5 rounded-lg border text-xs cursor-pointer transition-all ${
                        isSelected
                          ? "bg-white border-blue-500 shadow-sm ring-2 ring-blue-500/20"
                          : "bg-white/80 border-slate-200 hover:border-blue-300 hover:bg-white"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-1 mb-1">
                        <div className="flex items-center gap-1.5 truncate">
                          <span
                            className={`w-2 h-2 rounded-full ${
                              isSelected ? "bg-blue-600" : "bg-slate-300"
                            }`}
                          />
                          <span className="font-bold text-slate-800 font-mono truncate">
                            {it.sku || `Item #${idx + 1}`}
                          </span>
                        </div>
                        <span
                          className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded border shrink-0 ${badge.bg}`}
                        >
                          {badge.label}
                        </span>
                      </div>

                      <div className="text-[10px] text-slate-500 flex items-center justify-between pt-1 border-t border-slate-100">
                        <span>
                          Etapa:{" "}
                          <strong className="text-slate-700">
                            {it.analise.status === "PRONTO"
                              ? "PRONTO"
                              : it.analise.status === "EM_REVISAO"
                              ? "REVISÃO"
                              : it.analise.etapaMapeada}
                          </strong>
                        </span>
                        <span className="font-mono font-bold">
                          {it.analise.status === "PRONTO"
                            ? "✓ Concluído"
                            : it.analise.status === "EM_REVISAO"
                            ? "⏳ Em revisão"
                            : it.analise.atrasoEsperado > 0
                            ? `+${formatDiasUteis(
                                it.analise.atrasoEsperado
                              )} d.ú. atraso`
                            : `${formatDiasUteis(
                                it.analise.folga
                              )} d.ú. folga`}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 1. CARD DE DIAGNÓSTICO E RISCO DO ITEM SELECIONADO */}
          <div
            className={`border rounded-xl p-4 space-y-3 ${riscoAtual.cardBg} ${riscoAtual.border}`}
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span
                  className={`text-[10px] font-black uppercase px-2.5 py-1 rounded-full shadow-xs ${riscoAtual.bg}`}
                >
                  {riscoAtual.label}
                </span>
                <span className="font-bold text-slate-800 uppercase text-[11px]">
                  Status: {analise.status.replace("_", " ")}
                </span>
                {itemsAnalises.length > 1 && (
                  <span className="text-[10px] font-bold text-slate-500 bg-white/70 px-2 py-0.5 rounded border border-black/5">
                    (Item #{activeIndex + 1}: {sku || "Sem SKU"})
                  </span>
                )}
              </div>

              {/* Indicador de Atraso Esperado, Folga, Revisão ou Conclusão */}
              {analise.status === "PRONTO" ? (
                <span className="bg-emerald-100 text-emerald-800 border border-emerald-300 px-2.5 py-1 rounded-lg font-mono font-extrabold text-[11px] flex items-center gap-1">
                  ✓ Pronto na Oficina (Aguardando Envio)
                </span>
              ) : analise.status === "ENVIADO" ? (
                <span className="bg-blue-100 text-blue-800 border border-blue-300 px-2.5 py-1 rounded-lg font-mono font-extrabold text-[11px] flex items-center gap-1">
                  ✓ Pedido Despachado
                </span>
              ) : analise.status === "EM_REVISAO" ? (
                <span className="bg-amber-100 text-amber-900 border border-amber-300 px-2.5 py-1 rounded-lg font-mono font-extrabold text-[11px] flex items-center gap-1">
                  ⏳ Análise de Revisão Pendente
                </span>
              ) : analise.isVerificar ? (
                <span className="bg-indigo-100 text-indigo-900 border border-indigo-300 px-2.5 py-1 rounded-lg font-mono font-extrabold text-[11px] flex items-center gap-1">
                  🔄 Prazo Resetado (Verificação)
                </span>
              ) : analise.atrasoEsperado > 0 ? (
                <span className="bg-red-100 text-red-800 border border-red-200 px-2.5 py-1 rounded-lg font-mono font-extrabold text-[11px]">
                  ⚠ Atraso Esperado: +{formatDiasUteis(analise.atrasoEsperado)}{" "}
                  dias úteis
                </span>
              ) : analise.folga > 0 ? (
                <span className="bg-emerald-100 text-emerald-800 border border-emerald-200 px-2.5 py-1 rounded-lg font-mono font-extrabold text-[11px]">
                  ✓ Margem de Folga: {formatDiasUteis(analise.folga)} dias úteis
                </span>
              ) : null}
            </div>

            <p className="text-slate-700 leading-relaxed font-medium">
              {analise.descricaoRisco}
            </p>

            {fatorRisco > 0 && (
              <div className="text-[11px] text-slate-600 bg-white/70 p-2 rounded-lg border border-black/5 flex items-center justify-between">
                <span>Taxa histórica de retrabalho do modelo {modelo}:</span>
                <span className="font-mono font-bold text-slate-800">
                  {Math.round(fatorRisco * 100)}% das peças passam por ajuste
                </span>
              </div>
            )}
          </div>

          {/* 2. COMPARATIVO TEMPORAL DE DATAS DO ITEM SELECIONADO */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
            <h3 className="text-xs font-black text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
              <Calendar size={14} className="text-blue-600" />
              Cronograma com Dias da Semana
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Data da Compra */}
              <div className="bg-white p-3 rounded-lg border border-slate-200">
                <span className="text-[10px] font-bold text-slate-400 uppercase">
                  Data da Compra (Marco Zero)
                </span>
                <div className="font-bold text-slate-800 text-sm mt-0.5">
                  {formatDataComDiaSemana(dataCompra)}
                </div>
                <span className="text-[10px] text-slate-500 font-mono">
                  {formatDiasUteis(analise.decorrido)} dias úteis decorridos
                </span>
              </div>

              {/* Data Prometida (Venda) */}
              <div className="bg-white p-3 rounded-lg border border-slate-200">
                <span className="text-[10px] font-bold text-slate-400 uppercase">
                  Data Final Prometida (Acordo de Venda)
                </span>
                <div className="font-extrabold text-blue-700 text-sm mt-0.5">
                  {analise.dataPrometidaFormatada}
                </div>
                <span className="text-[10px] text-slate-500 font-mono">
                  Régua de {analise.prazoPrometido || "-"} dias úteis do modelo
                </span>
              </div>

              {/* Previsão Dinâmica de Entrega */}
              <div className="bg-white p-3 rounded-lg border border-slate-200 sm:col-span-2">
                <div className="flex justify-between items-start">
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase">
                      Previsão Dinâmica de Conclusão (Oficina)
                    </span>
                    <div
                      className={`font-extrabold text-sm mt-0.5 ${
                        analise.status === "PRONTO"
                          ? "text-emerald-700"
                          : analise.status === "EM_REVISAO"
                          ? "text-amber-700"
                          : analise.status === "ATRASADO" ||
                            analise.status === "VAI_ATRASAR"
                          ? "text-red-600"
                          : "text-emerald-700"
                      }`}
                    >
                      {analise.status === "PRONTO"
                        ? "Pronto na Oficina (Concluído)"
                        : analise.status === "EM_REVISAO"
                        ? "Aguardando conclusão da Revisão"
                        : analise.dataPrevistaFormatada}
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-[10px] font-bold text-slate-400 uppercase">
                      Tempo Restante na Oficina
                    </span>
                    <div className="font-mono font-bold text-slate-800 text-xs mt-0.5">
                      {analise.status === "PRONTO"
                        ? "0 dias úteis (Pronto)"
                        : analise.status === "EM_REVISAO"
                        ? "Indeterminado (Depende da revisão)"
                        : `~${formatDiasUteis(analise.faltam)} dias úteis restantes`}
                    </div>
                  </div>
                </div>

                <div className="text-[10px] text-slate-500 mt-2 pt-2 border-t border-slate-100 flex items-center justify-between">
                  <span>
                    {analise.status === "PRONTO"
                      ? "Peça finalizada na bancada da oficina. Pronta para envio/expedição."
                      : analise.status === "EM_REVISAO"
                      ? "O prazo final será recalculado assim que a revisão for concluída e o próximo status for definido."
                      : analise.isVerificar
                      ? `Prazo resetado para SOLICITACAO (Aguardando Análise). Faltam ~${formatDiasUteis(
                          analise.faltam
                        )} dias úteis para percorrer a esteira completa.`
                      : `Fórmula: ${formatDiasUteis(
                          analise.decorrido
                        )} decorridos + ~${formatDiasUteis(
                          analise.faltam
                        )} restantes = ${formatDiasUteis(
                          analise.previstoDias
                        )} dias úteis`}
                  </span>
                  <span>
                    Etapa base atual:{" "}
                    <strong>
                      {analise.status === "PRONTO"
                        ? "PEDIDO PRONTO"
                        : analise.status === "EM_REVISAO"
                        ? "REVISÃO"
                        : analise.etapaMapeada || "-"}
                    </strong>
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* 3. LINHA DO TEMPO DA ESTEIRA CANÔNICA DE ETAPAS */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
            <h3 className="text-xs font-black text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
              <Activity size={14} className="text-indigo-600" />
              Esteira Produtiva do Item ({sku || `Item #${activeIndex + 1}`})
            </h3>

            <div className="relative border-l-2 border-slate-200 ml-3 space-y-4 py-1">
              {ETAPAS_CANONICAS.map((etp, idx) => {
                const isPassada = isProntoOuEnviado
                  ? true
                  : currentIndex !== -1 && idx < currentIndex;
                const isAtual =
                  !isProntoOuEnviado &&
                  (idx === currentIndex || etp === etapaAtual);
                const isFutura =
                  !isProntoOuEnviado &&
                  currentIndex !== -1 &&
                  idx > currentIndex;

                // Tenta achar timestamp de quando passou por esta etapa
                const transitionLog = transitions.find((t) => t.to === etp);

                return (
                  <div key={etp} className="relative pl-6">
                    {/* Marcador na linha */}
                    <div
                      className={`absolute -left-[9px] top-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        isPassada
                          ? "bg-emerald-500 border-emerald-500 text-white"
                          : isAtual
                          ? "bg-blue-600 border-blue-600 text-white animate-pulse"
                          : "bg-white border-slate-300 text-transparent"
                      }`}
                    >
                      {isPassada && <CheckCircle2 size={10} />}
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-1">
                      <div className="flex items-center gap-2">
                        <span
                          className={`font-bold ${
                            isAtual
                              ? "text-blue-700 font-extrabold text-xs"
                              : isPassada
                              ? "text-slate-700"
                              : "text-slate-400"
                          }`}
                        >
                          {PRODUCTION_STATUS_CONFIG[etp]?.label || etp}
                        </span>

                        {isAtual && (
                          <span
                            className={`px-1.5 py-0.5 rounded text-[9px] font-black uppercase ${
                              analise.isVerificar
                                ? "bg-amber-100 text-amber-800"
                                : analise.isRevisao
                                ? "bg-amber-100 text-amber-800"
                                : "bg-blue-100 text-blue-700"
                            }`}
                          >
                            {analise.isVerificar
                              ? "Resetado por Verificação"
                              : analise.isRevisao
                              ? "Pendente de Revisão"
                              : "Etapa Atual"}
                          </span>
                        )}
                      </div>

                      {/* Informações de tempo */}
                      <div className="text-[10px] font-mono text-slate-400">
                        {transitionLog ? (
                          <span className="text-emerald-700 font-bold">
                            Concluído em {transitionLog.dateFormatted} (
                            {transitionLog.author})
                          </span>
                        ) : isPassada ? (
                          <span className="text-emerald-600 font-medium">
                            Concluído
                          </span>
                        ) : isAtual && analise.faltam > 0 ? (
                          <span className="text-blue-600 font-bold">
                            Faltam ~{formatDiasUteis(analise.faltam)} dias úteis
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Se o pedido estiver pronto ou enviado, exibe o marco final da oficina */}
              {isProntoOuEnviado && (
                <div className="relative pl-6 pt-1">
                  <div className="absolute -left-[9px] top-1.5 w-4 h-4 rounded-full border-2 bg-emerald-600 border-emerald-600 text-white flex items-center justify-center">
                    <CheckCircle2 size={10} />
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-1">
                    <div className="flex items-center gap-2">
                      <span className="font-extrabold text-emerald-800 text-xs">
                        {analise.status === "ENVIADO"
                          ? "PEDIDO ENVIADO"
                          : "PEDIDO PRONTO"}
                      </span>
                      <span className="bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded text-[9px] font-black uppercase">
                        {analise.status === "ENVIADO"
                          ? "Despachado"
                          : "Pronto para Envio"}
                      </span>
                    </div>
                    <span className="text-[10px] font-mono text-emerald-700 font-bold">
                      Oficina 100% Concluída
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 4. HISTÓRICO DE LOGS DE TRANSIÇÃO REGISTRADOS */}
          {transitions.length > 0 && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2">
              <h3 className="text-xs font-black text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                <History size={14} className="text-slate-500" />
                Registros de Movimentação do Chão de Fábrica ({sku || `Item #${activeIndex + 1}`})
              </h3>
              <div className="space-y-1.5 max-h-36 overflow-y-auto custom-scrollbar">
                {transitions.map((t, i) => (
                  <div
                    key={i}
                    className="bg-white p-2 rounded-lg border border-slate-100 flex items-center justify-between text-[10px]"
                  >
                    <span className="text-slate-700 font-medium">
                      {t.from ? `${t.from} ➔ ` : ""}
                      {t.to}
                    </span>
                    <div className="text-slate-400 font-mono flex items-center gap-2">
                      <span>{t.author}</span>
                      <span>•</span>
                      <span>{t.dateFormatted}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* FOOTER DO MODAL */}
        <div className="p-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          <div className="text-[11px] text-slate-500">
            {itemsAnalises.length > 1 && (
              <span>
                Visualizando item <strong>{activeIndex + 1}</strong> de{" "}
                <strong>{itemsAnalises.length}</strong>
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="px-5 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-xl text-xs font-bold shadow-sm transition-all cursor-pointer"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
